import { faArrowRight, faShareSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FormikProps } from "formik";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { convertFieldValuesToStrings, convertFieldValuesToTypes } from "../common/utils";
import { useBoundaryService } from "../services/boundary.service";
import { useDeficiencyReviewService } from "../services/deficiency-review.service";
import { useDeficiencyService } from "../services/deficiency.service";
import { useWorkValidation } from "../hooks/useWorkValidation";
import { BoundaryPoint } from "../types/boundary.types";
import { Deficiency, DeficiencyReview, RecordIdType, RuleExpressionAndWork } from "../types/deficiency.types";
import { RuleSummary } from "../types/rule.types";
import { FinishWorkDTO, RegisteredVoterFlags, ValidationForm, WorkDto, WorkField, WorkStatus } from "../types/work.types";
import CirculatorView from "./circulator-view.component";
import FieldValidation from "./field-validation.component";
import { InlineSpinner } from "./inline-spinner";
import { Loader } from "./loader";
import { OutOfBoundsMap } from "./out-of-bounds-map.component";
import SignatoryView from "./signatory-view.component";
import ValidationWarningDialog from "./validation-warning-dialog.component";
import { WorkForTaskType } from "./work-for-task-type";
import WorkBreakResume from "./work-break-resume.component";

export type DeficiencyViewProps = {
  matterId: string;
  deficiencyId: string;
  getNextDeficiencyReview: () => void;
  onNavigate: (sheetNumber: number) => void;
  onFinished: () => void;
  onWorkLoaded?: (work: WorkDto) => void;
  onTakeBreak?: () => void;
  isOnBreak?: boolean;
};

export function DeficiencyView({
  matterId,
  deficiencyId,
  getNextDeficiencyReview,
  onNavigate,
  onFinished,
  onWorkLoaded,
  onTakeBreak,
  isOnBreak = false,
}: DeficiencyViewProps) {
  const navigate = useNavigate();
  const { doesBoundaryExist } = useBoundaryService();
  const { getDeficiencyWithRuleById } = useDeficiencyService();
  const { postDeficiencyReview } = useDeficiencyReviewService();
  const [deficiency, setDeficiency] = useState<Deficiency | null>(null);
  const [rule, setRule] = useState<RuleSummary | null>(null);
  const [ruleExpressionAndWorks, setRuleExpressionAndWorks] = useState<RuleExpressionAndWork[]>([]);
  const formikRef = useRef<FormikProps<ValidationForm>>(null);
  const restoreSearchParamsRef = useRef<((params: Record<string, string>) => void) | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeficient, setIsDeficient] = useState<boolean>(true);
  const [note, setNote] = useState<string>("");
  const [isBoundary, setIsBoundary] = useState<boolean>(false);
  const [geographicPoint, setGeographicPoint] = useState<BoundaryPoint>();

  const loadDeficiencies = useCallback(() => {
    if (matterId && deficiencyId && !isOnBreak) {
      setIsLoading(true);
      getDeficiencyWithRuleById(matterId, deficiencyId).then((data) => {
        setIsLoading(false);
        setRule(data.rule);
        setDeficiency(data.deficiency);
        setIsDeficient(data.deficiency.isDeficient);
        setNote(data.deficiency.note || "");
        if (data.expressionAndWorks.length === 0) {
          return;
        }

        const modifiedExpressionAndWorks = data.expressionAndWorks.map((ew) => {
          if (ew.work?.fields) {
            ew.work.fields = convertFieldValuesToStrings(ew.work?.fields ?? []);
          }
          return ew;
        });
        setRuleExpressionAndWorks(modifiedExpressionAndWorks);

        const firstItemWithWork = modifiedExpressionAndWorks?.find((e) => e.work);
        if (firstItemWithWork?.work && onWorkLoaded) {
          onWorkLoaded(firstItemWithWork.work);
        }
        const matterVariable = modifiedExpressionAndWorks[1].matterVariable;
        if (matterVariable?.value) {
          doesBoundaryExist(3, matterVariable?.value)
            .then(() => {
              setGeographicPoint(modifiedExpressionAndWorks[0].signatoryPoint);
              setIsBoundary(true);
            })
            .catch(() => {
              setIsBoundary(false);
            });
        }
      });
    }
  }, [matterId, deficiencyId, isOnBreak, getDeficiencyWithRuleById, onWorkLoaded, doesBoundaryExist]);

  const {
    submitWorkIfDirty,
    handleValidationOverride,
    handleValidationCancel,
    showValidationWarning,
    validationWarningMessage,
    validationSearchParams,
  } = useWorkValidation({
    setLoading: setIsLoading,
    onError: (msg) => console.error(msg),
    onSuccess: loadDeficiencies,
  });

  useEffect(() => {
    loadDeficiencies();
  }, [loadDeficiencies]);

  // Restore search params when validation warning appears with params from backend
  useEffect(() => {
    if (showValidationWarning && validationSearchParams && restoreSearchParamsRef.current) {
      restoreSearchParamsRef.current(validationSearchParams);
    }
  }, [showValidationWarning, validationSearchParams]);

  const handleNext = useCallback(
    async (work: WorkDto, fields: WorkField[], externalDataRecordId?: string | null, registeredVoterFlags?: RegisteredVoterFlags, searchParams?: Record<string, string>) => {
      if (!work.workId) {
        return Promise.reject("No work");
      }

      const workData: FinishWorkDTO = {
        workStatusId: WorkStatus.Completed,
        fields: convertFieldValuesToTypes(fields),
        externalDataRecordId: externalDataRecordId ?? (work.externalDataRecordId?.toString() || null),
        registeredVoterFlags: registeredVoterFlags ?? (work.registeredVoterFlags || RegisteredVoterFlags.Unknown),
        searchParams,
      };

      // NOTE: Do NOT update work.fields in parent state during submission.
      // The form's Formik state maintains the edited values.
      // Updating parent state would trigger the child form's auto-search effect,
      // which would make a new API call and overwrite the form's current state,
      // causing it to appear "reset" when the validation dialog appears.

      try {
        await submitWorkIfDirty(work.workId.toString(), workData, formikRef);
      } catch (error) {
        throw error;
      }
    },
    [submitWorkIfDirty]
  );


  const handleReviewDeficiency = async () => {
    setIsLoading(true);
    try {
      const deficiencyReview = await postDeficiencyReview(matterId, {
        deficiencyId: parseInt(deficiencyId, 10),
        isDeficient,
        note,
      } as DeficiencyReview);

      if (deficiencyReview.deficiencyId) {
        navigate(`/admin/matters/${matterId}/deficiencies/review/${deficiencyReview.deficiencyId}`);
      } else {
        onFinished();
      }
    } catch (error) {
      console.error("Error reviewing deficiency:", error);
      onFinished();
    } finally {
      setIsLoading(false);
    }
  };

  function handleCheckboxChange(event: ChangeEvent<HTMLInputElement>): void {
    const isChecked = event.target.checked;
    setIsDeficient(isChecked);
  }

  const renderSide = useCallback(
    (ruleExpressionAndWork: RuleExpressionAndWork, index: number) => {
      const ruleExpression = ruleExpressionAndWork.expression;
      const work = ruleExpressionAndWork.work;
      const circulator = ruleExpressionAndWork.circulator;
      const signatory = ruleExpressionAndWork.signatory;
      const matterVariable = ruleExpressionAndWork.matterVariable;
      const isMatterVariable = ruleExpression?.startsWith("Matter.Variables[");
      const isCell = ruleExpression?.startsWith("Row.Cells[");
      const isField = ruleExpression?.startsWith("Sheet.Fields[");
      const isRegisteredVoter = ruleExpression?.startsWith("Row.RegisteredVoter.");

      if (isMatterVariable) {
        if (!matterVariable) return null;
        return (
          <>
            <p>{`Parameter: ${matterVariable.key}`}</p>
            <label>{matterVariable.key}</label>
            <input type="text" defaultValue={matterVariable.value} />
            {isBoundary && !!geographicPoint && (
              <div>
                <OutOfBoundsMap
                  outerBoundaryName={matterVariable.value}
                  innerBoundaryName={geographicPoint.pointName}
                  point={geographicPoint}
                />
              </div>
            )}
          </>
        );
      } else if ((isCell && work) || (isField && work)) {
        return (
          <>
            {work.sheetNumber && (
              <span>
                {`Sheet ${work.sheetNumber}`}
                {work?.rowNumber ? ` - Row ${work.rowNumber}` : ""}
                {work?.fields && work.fields.length > 0
                  ? ` - ${work.fields.map((f) => f.name).join(" | ")}`
                  : work?.fieldNumber
                  ? ` - Field ${work.fieldNumber}`
                  : ""}
              </span>
            )}
            <FieldValidation
              work={work}
              formikRef={formikRef}
              onNext={(fields) => handleNext(work, fields)}
              isSignatureTable={isCell}
              isRegistrationVerification={false}
              nextLabel="Submit Correction"
            />
          </>
        );
      } else if ((isRegisteredVoter && work) || (deficiency?.recordIdType === RecordIdType.SignatureSheetRow && work)) {
        return (
          <>
            {work.sheetNumber && (
              <span>
                {work?.matterName ? `${work.matterName} - ` : ""}
                {`Sheet ${work.sheetNumber}`}
                {work?.rowNumber ? ` - Row ${work.rowNumber}` : ""}
              </span>
            )}
            <WorkForTaskType
              work={work}
              formikRef={formikRef}
              onNext={(fields, externalDataRecordId, registeredVoterFlags, searchParams) =>
                handleNext(work, fields, externalDataRecordId, registeredVoterFlags, searchParams)
              }
              onSave={(fields, externalDataRecordId, registeredVoterFlags, searchParams) =>
                handleNext(work, fields, externalDataRecordId, registeredVoterFlags, searchParams)
              }
              onRestoreSearchParams={(restoreCallback) => {
                restoreSearchParamsRef.current = restoreCallback;
              }}
              nextLabel="Submit Correction"
            />
          </>
        );
      } else if (ruleExpression?.includes("FormLines")) {
        // We don't have one for this yet
        return (
          <div>
            <p>FormLines work: {ruleExpression}</p>
          </div>
        );
      } else if (ruleExpression?.includes("Sheet.Circulator") && circulator) {
        return (
          <CirculatorView
            circulator={circulator}
            matterId={parseInt(matterId)}
            sheetNumber={deficiency?.sheetNumber || undefined}
            deficiencyId={deficiencyId}
          />
        );
      } else if (ruleExpression?.includes("Row.Signatory") && signatory) {
        return <SignatoryView signatory={signatory} />;
      } else {
        return (
          <div>
            <p>Rule Expression: {ruleExpression}</p>
          </div>
        );
      }
    },
    [deficiency?.recordIdType, deficiency?.sheetNumber, geographicPoint, handleNext, isBoundary, matterId, deficiencyId]
  );

  if (isLoading || !rule || !deficiency) {
    return <Loader />;
  }

  if (isOnBreak) {
    return (
      <WorkBreakResume
        isOnBreak={isOnBreak}
        loading={isLoading}
        onResume={() => {
          if (onTakeBreak) onTakeBreak();
        }}
      />
    );
  }

  return (
    <div className="m-4">
      <div className="row">
        <div className="col">{rule?.name}</div>
        {(ruleExpressionAndWorks.at(-1)?.expression || ruleExpressionAndWorks.at(-1)?.work) && (
          <div className="col-6">{rule?.operationName}</div>
        )}
      </div>
      <div className="row">
        {ruleExpressionAndWorks
          .filter((expressionAndWork) => expressionAndWork.expression || expressionAndWork.work)
          .map((expressionAndWork, index) => (
            <div key={index} className="col">
              {renderSide(expressionAndWork, index)}
            </div>
          ))}
      </div>
      <hr />
      <textarea
        placeholder="Add deficiency note here"
        className="form-control"
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <hr />
      <div className="row">
        <div className="col d-flex justify-content-start align-items-end">
          {onTakeBreak && (
            <button className="btn btn-sw btn-outline" onClick={onTakeBreak}>
              <FontAwesomeIcon icon={faShareSquare} className="me-2" />
              Take a Break
            </button>
          )}
        </div>
        <div className="col d-flex flex-column align-items-center gap-2">
          <div>
            <input id="isDeficient" type="checkbox" checked={isDeficient} onChange={handleCheckboxChange}></input>
            <label className="ms-2" htmlFor="isDeficient">
              Is Deficient
            </label>
          </div>
          <button className="btn btn-sw btn-outline" onClick={handleReviewDeficiency} disabled={isLoading}>
            {!isLoading && <FontAwesomeIcon icon={faArrowRight} className="me-2" />}
            {isLoading && <InlineSpinner />}
            Review Complete
          </button>
        </div>
        <div className="col"></div>
      </div>

      <ValidationWarningDialog
        show={showValidationWarning}
        errorMessage={validationWarningMessage}
        onCancel={handleValidationCancel}
        onOverride={handleValidationOverride}
      />
    </div>
  );
}
